// Ship From Locations — shared types and API calls for the address book and the
// per-store assignment table (migration 295). The sender name printed on a label
// comes from the store (or its shop name), so addresses carry no name.
import { API_BASE } from '../../../lib/api'

export type ShipFromAddress = {
  name?: string | null
  line1: string
  line2?: string | null
  city: string
  state: string
  postal_code: string
  country_code: string
  phone?: string | null
}

export type ShipFromLocation = {
  id: string
  name: string
  address: ShipFromAddress
  is_default: boolean
  active: boolean
  created_at: string
  updated_at: string
}

export type StoreShipFrom = {
  store_integration_id: string
  shop_name: string
  provider: string
  org_name: string
  active: boolean
  ship_from_location_id: string | null
  ship_from_name: string | null
  has_slip_logo: boolean
}

export type StoreAssignment = Pick<StoreShipFrom, 'ship_from_location_id' | 'ship_from_name'>

export const LOCATIONS_QUERY_KEY = ['ship-from-locations'] as const
export const STORE_ASSIGNMENTS_QUERY_KEY = ['ship-from-locations', 'store-assignments'] as const

const base = `${API_BASE}/admin/ship-from-locations`

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(`${res.status}`)
  return res.json() as Promise<T>
}

export async function fetchLocations(): Promise<{ items: ShipFromLocation[]; total: number }> {
  return json(await fetch(base))
}

export async function fetchStoreAssignments(): Promise<{ items: StoreShipFrom[]; total: number }> {
  return json(await fetch(`${base}/store-assignments`))
}

export async function saveStoreAssignment(
  storeId: string,
  body: StoreAssignment,
): Promise<StoreShipFrom> {
  return json(
    await fetch(`${base}/store-assignments/${storeId}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),
  )
}

const storeBase = `${API_BASE}/store-integrations`

/** Upload a packing-slip logo for a store (multipart image). */
export async function uploadStoreSlipLogo(
  storeId: string,
  file: File,
): Promise<{ has_slip_logo: boolean }> {
  const form = new FormData()
  form.append('file', file)
  return json(await fetch(`${storeBase}/${storeId}/slip-logo`, { method: 'PUT', body: form }))
}

/** Remove a store's packing-slip logo. */
export async function clearStoreSlipLogo(storeId: string): Promise<{ has_slip_logo: boolean }> {
  return json(await fetch(`${storeBase}/${storeId}/slip-logo`, { method: 'DELETE' }))
}

/** One-line address a person can recognise: "420 Kuller Rd, Clifton NJ 07011". */
export function formatAddress(address: ShipFromAddress): string {
  const street = [address.line1, address.line2].filter(Boolean).join(' ')
  const locality = [address.city, address.state, address.postal_code].filter(Boolean).join(' ')
  return [street, locality].filter(Boolean).join(', ')
}
